// marketPulseFormatters.ts
// Shared formatting utilities for Market Pulse UI components.

export type FreshnessLevel = "live" | "partial" | "stale" | "degraded" | "unavailable";

// Safe text extraction
export function safeText(raw: unknown, fallback = "Unavailable"): string {
  if (!raw || typeof raw !== "string") return fallback;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return fallback;
  if (trimmed.length > 300) return trimmed.slice(0, 297) + "…";
  return trimmed;
}

// Known reason code to compact user-facing label
const REASON_CODE_MAP: Record<string, string> = {
  CALENDAR_PROVIDER_NOT_CONNECTED: "Calendar unavailable",
  CALENDAR_PROVIDER_UNAVAILABLE: "Calendar unavailable",
  EQUITY_QUOTE_PROVIDER_NOT_CONNECTED: "Equity quotes degraded",
  EQUITY_QUOTE_PROVIDER_UNAVAILABLE: "Equity quotes degraded",
  NEWS_PROVIDER_NOT_CONNECTED: "News unavailable",
  NEWS_PROVIDER_UNAVAILABLE: "News unavailable",
  NO_MARKET_DATA: "No market data",
  TICKER_CACHE_STALE: "Ticker cache stale",
  FOREX_PROVIDER_NOT_CONNECTED: "Forex feed degraded",
  FOREX_PROVIDER_UNAVAILABLE: "Forex feed degraded",
  VIX_CONTEXT_PROVIDER_UNAVAILABLE: "Volatility context unavailable",
  PROVIDER_TIMEOUT: "Provider timed out",
  RATE_LIMITED: "Rate limited",
  DATA_NOT_FOUND: "Data not found",
  PROVIDER_ERROR: "Provider error",
};

export function compactReason(raw: unknown): string {
  const s = typeof raw === "string" ? raw.trim() : String(raw ?? "").trim();
  if (!s) return "Unavailable";
  // Try exact match first (normalise to upper)
  const upper = s.toUpperCase().replace(/-/g, "_");
  if (REASON_CODE_MAP[upper]) return REASON_CODE_MAP[upper];
  // Keyword fallbacks
  if (/calendar/i.test(s)) return "Calendar unavailable";
  if (/equity|equities/i.test(s)) return "Equity quotes degraded";
  if (/news/i.test(s)) return "News unavailable";
  if (/forex/i.test(s)) return "Forex feed degraded";
  if (/vix/i.test(s)) return "Volatility context unavailable";
  if (/timeout/i.test(s)) return "Provider timed out";
  if (/rate.?limit/i.test(s)) return "Rate limited";
  if (/provider/i.test(s)) return "Provider unavailable";
  if (/cache|stale/i.test(s)) return "Using cached data";
  if (/404|not.?found/i.test(s)) return "Data not found";
  if (/500|internal/i.test(s)) return "Provider error";
  return "Unavailable";
}

// Age in seconds to human label
export function formatFreshness(age_s: number | null | undefined): string {
  if (age_s == null) return "Unknown";
  if (age_s < 10) return "just now";
  if (age_s < 60) return `${Math.round(age_s)}s ago`;
  if (age_s < 3600) return `${Math.round(age_s / 60)}m ago`;
  return `${Math.round(age_s / 3600)}h ago`;
}

// Summarise any status-like value to FreshnessLevel
export function summarizeStatus(s: unknown): FreshnessLevel {
  const str = (typeof s === "string" ? s : String(s ?? "")).toLowerCase().trim();
  if (!str || str === "null" || str === "undefined") return "unavailable";
  if (str === "live" || str === "ok" || str === "pass" || str === "fresh" || str === "healthy" || str.includes("live")) return "live";
  if (str === "partial") return "partial";
  if (str === "stale" || str === "cached" || str === "warm" || str.includes("stale") || str.includes("cache")) return "stale";
  if (str === "degraded" || str.includes("warn") || str.includes("degrad") || str.includes("caution")) return "degraded";
  return "unavailable";
}

export const STATUS_COLORS: Record<FreshnessLevel, string> = {
  live: "#22c55e",
  partial: "#eab308",
  stale: "#f59e0b",
  degraded: "#f97316",
  unavailable: "#6b7280",
};

export const STATUS_LABELS: Record<FreshnessLevel, string> = {
  live: "Live",
  partial: "Partial Data",
  stale: "Stale",
  degraded: "Degraded",
  unavailable: "Unavailable",
};

// Exclude items where tradable/is_tradable/tradeable === false
export function filterTradable<T extends Record<string, unknown>>(items: T[]): T[] {
  return items.filter(
    (i) =>
      i["tradable"] !== false &&
      i["is_tradable"] !== false &&
      i["tradeable"] !== false &&
      i["trading_enabled"] !== false
  );
}

export function formatPrice(price: unknown): string {
  const n = typeof price === "number" ? price : Number(price);
  if (price == null || isNaN(n)) return "—";
  if (n >= 1000) return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // forex / small prices
  if (n < 10) return n.toFixed(4);
  return `$${n.toFixed(2)}`;
}

export function formatChangePct(pct: unknown): string {
  const n = typeof pct === "number" ? pct : Number(pct);
  if (pct == null || isNaN(n)) return "—";
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

// Maps raw permission string to clean label — never shows "Unknown"
export function formatPermission(raw: unknown): string {
  const s = (typeof raw === "string" ? raw : String(raw ?? "")).toLowerCase().trim();
  if (s === "allowed" || s === "allow") return "Trading Permission: Allowed";
  if (s === "caution" || s === "allow_with_caution") return "Trading Permission: Caution";
  if (s === "blocked" || s === "block" || s === "denied" || s === "block_new_entries") return "Trading Permission: Blocked";
  // Anything else (including empty, null, unknown) → Caution
  return "Trading Permission: Caution";
}

// Maps raw risk posture to clean label
export function formatRiskPosture(raw: unknown): string {
  const s = (typeof raw === "string" ? raw : String(raw ?? "")).toLowerCase().trim();
  if (s === "normal") return "Normal";
  if (s === "reduced") return "Reduced";
  if (s === "defensive") return "Defensive";
  return "Degraded";
}

// Groups a flat mover array by asset_class field
export function groupMoversByClass(movers: Record<string, unknown>[]): Record<string, Record<string, unknown>[]> {
  const result: Record<string, Record<string, unknown>[]> = {};
  for (const m of movers) {
    const cls = (typeof m["asset_class"] === "string" ? m["asset_class"] : "other").toLowerCase();
    if (!result[cls]) result[cls] = [];
    result[cls].push(m);
  }
  return result;
}

// Clean data health label for a named source — non-critical sources noted
const NON_CRITICAL_SOURCES = new Set(["news", "calendar"]);

export function healthLabel(source: string, status: unknown): string {
  const s = (typeof status === "string" ? status : String(status ?? "")).toLowerCase();
  const src = source.toLowerCase();
  const isOk = s === "live" || s === "ok" || s === "pass";
  const isDeg = s === "degraded" || s === "partial" || s.includes("degrad");
  const label = src.charAt(0).toUpperCase() + src.slice(1);
  const nonCritical = NON_CRITICAL_SOURCES.has(src) ? " (non-critical)" : "";
  if (isOk) return `${label}: Live`;
  if (isDeg) return `${label}: Degraded${nonCritical}`;
  return `${label}: Unavailable${nonCritical}`;
}

// Backward compat aliases
export type NormalizedStatus = FreshnessLevel;

export function statusColor(s: FreshnessLevel): string {
  return STATUS_COLORS[s];
}

export function statusLabel(s: FreshnessLevel): string {
  return STATUS_LABELS[s];
}

export function filterTradableItems<T extends Record<string, unknown>>(items: T[]): T[] {
  return filterTradable(items);
}

export function formatStrategyImpact(ctx: Record<string, unknown> | null | undefined): {
  permission: string;
  posture: string;
  summary: string;
} {
  if (!ctx) return { permission: "Caution", posture: "Normal", summary: "Strategy context unavailable." };
  const perm = String(ctx["trading_permission"] ?? ctx["permission"] ?? ctx["status"] ?? "caution");
  const posture = String(ctx["risk_posture"] ?? ctx["posture"] ?? "Normal");
  const summary = safeText(ctx["summary"] ?? ctx["description"] ?? ctx["context"], "No strategy context available.");
  return { permission: perm, posture, summary };
}

// User-facing label for the equity universe source.
// Backend ships raw enum strings (BROKER_DISCOVERY / CACHE / CONFIG_FALLBACK).
// This wraps them for display so the UI never shows snake_case enums.
export function formatUniverseSource(raw: unknown): string {
  if (!raw || typeof raw !== "string") return "Unknown";
  const map: Record<string, string> = {
    BROKER_DISCOVERY: "Broker discovery",
    CACHE: "Cache",
    CONFIG_FALLBACK: "Config fallback",
  };
  return map[raw.toUpperCase()] ?? raw.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Format an ISO 8601 timestamp string as "May 5, 3:24 PM ET" (compact form).
// Returns "" for invalid / missing input rather than a raw string.
export function formatLastUpdated(value: unknown): string {
  if (!value || typeof value !== "string") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }) + " ET";
  } catch {
    return "";
  }
}

// One-line global disclaimer for the regime grid footer. Replaces the
// per-card boilerplate that was making every regime card identical.
export const REGIME_FOOTNOTE = "Regime context is advisory; account profile and hard risk limits remain authoritative.";

